import {
  Layout, Model, type TabNode, type TabSetNode, type BorderNode, type ITabSetRenderValues, type ITabRenderValues, type Action, Actions, type Node,
  type IJsonModel, type ILayoutApi,
} from 'flexlayout-react';
import 'flexlayout-react/style/dark.css';
import React, {
  useRef, useEffect, useCallback,
} from 'react'; // Add import statement for 'React'
import {useDispatch, useSelector} from 'react-redux';
import {debounce} from 'lodash';
import {LoadingIndicator, QepQueryClientProvider} from '@qep/shared/src';
import {safeParseSchema} from '@qep/shared/src/schemas/custom-schema-validators';
import {
  getApiServer,
  getAppEnv,
  getFileServer,
  getMdsWsServer,
  getOasWsServer,
  getWebSocketServer,
  isPreviewMode,
  isWorkspaceTabRenderOnDemand,
} from '@qep/shared/src/utils/runtime-config';
import {type QepComponent} from '@qep/preloads/src/preloads/QEP/containers/Browser/communication/types.js';
import {
  orderBlotterAddTabMessageSchema, windowInitializeRequestSchema, windowInitializeResponseSchema,
} from '@qep/preloads/src/preloads/QEP/contracts/runtime-messages.js';
import {
  resolveHttpOrigin,
  resolveTrustedFrontendOrigins,
} from '@qep/preloads/src/preloads/QEP/contracts/message-boundary-security.js';
import AddSVG from 'jsx:../../assets/add.svg';
import {selectWorkspaceChange, saveWorkspaceState, loadAlgoDefinitions} from '../state/reducer.js';
import {type AppDispatch} from '../state/store.js';
import {ComponentSelector} from '../components/component-selector/component-selector.js';
import {Toolbar} from '../components/toolbar/toolbar.js';
import {getContainer, toTabNode} from '../utils/utils.js';
import Linker from '../components/linker/linker.js';
import {DefaultLinkerGroupKeys} from '../communication/types.js';
import Chat from '../components/chat/chat.js';
import './window-container.scss';
import {onWindowSelected, onWindowClosed} from './window-utils.js';

const fields = ['Name', 'Field1', 'Field2', 'Field3', 'Field4', 'Field5'];
const allowedWorkspaceHtmlTags = new Set([
  'a',
  'b',
  'blockquote',
  'br',
  'code',
  'div',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'i',
  'li',
  'ol',
  'p',
  'pre',
  's',
  'span',
  'strong',
  'table',
  'tbody',
  'td',
  'th',
  'thead',
  'tr',
  'u',
  'ul',
]);
const allowedWorkspaceHtmlAttributes = new Set(['class', 'colspan', 'href', 'rel', 'rowspan', 'target', 'title']);
const allowedWorkspaceLinkProtocols = new Set(['http:', 'https:', 'mailto:']);

/**
 * Reports whether a workspace link resolves to an explicitly supported non-script protocol.
 */
const isSafeWorkspaceLink = (rawHref: string): boolean => {
  try {
    // eslint-disable-next-line no-control-regex -- Browser URL parsing ignores these obfuscating control characters.
    const compactHref = rawHref.replaceAll(/[\u0000-\u0020\u007F-\u009F]/g, '');
    const url = new URL(compactHref, document.baseURI);
    return allowedWorkspaceLinkProtocols.has(url.protocol);
  } catch {
    return false;
  }
};

/**
 * Removes executable workspace markup while preserving the supported formatting allowlist.
 */
const sanitizeWorkspaceHtml = (rawHtml: string): string => {
  const parsedDocument = new DOMParser().parseFromString(rawHtml, 'text/html');

  for (const element of parsedDocument.body.querySelectorAll('*')) {
    const tagName = element.tagName.toLowerCase();
    if (!allowedWorkspaceHtmlTags.has(tagName)) {
      element.replaceWith(parsedDocument.createTextNode(element.textContent ?? ''));
      continue;
    }

    // Snapshot the live attribute collection because unsafe attributes are removed during iteration.
    const attributes = [...element.attributes];
    for (const attribute of attributes) {
      const attributeName = attribute.name.toLowerCase();
      const isUnsafeLink = attributeName === 'href' && !isSafeWorkspaceLink(attribute.value);
      if (!allowedWorkspaceHtmlAttributes.has(attributeName) || isUnsafeLink) {
        element.removeAttribute(attribute.name);
      }
    }

    if (tagName === 'a' && element.hasAttribute('href')) {
      element.setAttribute('target', '_blank');
      element.setAttribute('rel', 'noopener noreferrer');
    }
  }

  return parsedDocument.body.innerHTML;
};

type EditableTabLabelProperties = {
  node: TabNode;
};

/**
 * Renders a tab label that owns rename commit/cancel behavior outside of FlexLayout internals.
 */
export const EditableTabLabel = ({node}: EditableTabLabelProperties) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [draftName, setDraftName] = React.useState(node.getName());
  const inputRef = React.useRef<HTMLInputElement | undefined>(undefined);
  const draftNameRef = React.useRef(draftName);

  useEffect(() => {
    draftNameRef.current = draftName;
  }, [draftName]);

  useEffect(() => {
    if (!isEditing) {
      setDraftName(node.getName());
    }
  }, [isEditing, node]);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  /**
   * Commits a changed tab name through the FlexLayout model and exits edit mode.
   */
  const commitEditing = useCallback((nextName: string) => {
    setIsEditing(false);
    const committedName = nextName.trim().length === 0 ? node.getName() : nextName;
    if (committedName !== node.getName()) {
      node.getModel().doAction(Actions.renameTab(node.getId(), committedName));
    }
  }, [node]);

  /**
   * Cancels editing and restores the current model name.
   */
  const cancelEditing = useCallback(() => {
    setDraftName(node.getName());
    setIsEditing(false);
  }, [node]);

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    /**
     * Commits when focus leaves the textbox because the user clicked elsewhere in the layout.
     */
    const handlePointerDown = (event: PointerEvent) => {
      if (event.target !== inputRef.current) {
        commitEditing(draftNameRef.current);
      }
    };

    /**
     * Commits when browser focus moves into an iframe, which does not always produce an outer document click.
     */
    const handleWindowBlur = () => {
      const {activeElement} = document;
      if (activeElement instanceof HTMLElement && activeElement.tagName === 'IFRAME') {
        commitEditing(draftNameRef.current);
      }
    };

    document.body.addEventListener('pointerdown', handlePointerDown);
    globalThis.addEventListener('blur', handleWindowBlur);

    return () => {
      document.body.removeEventListener('pointerdown', handlePointerDown);
      globalThis.removeEventListener('blur', handleWindowBlur);
    };
  }, [commitEditing, isEditing]);

  /**
   * Starts label editing without allowing the double-click to select or drag the tab.
   */
  const handleDoubleClick = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDraftName(node.getName());
    setIsEditing(true);
  };

  /**
   * Keeps pointer events inside the text box from reaching FlexLayout's tab drag handlers.
   */
  const handleInputPointerDown = (event: React.PointerEvent<HTMLInputElement>) => {
    event.stopPropagation();
  };

  /**
   * Applies keyboard semantics that match the previous FlexLayout patch.
   */
  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.code === 'Escape') {
      cancelEditing();
      event.stopPropagation();
      return;
    }

    if (event.code === 'Enter' || event.code === 'NumpadEnter') {
      commitEditing(event.currentTarget.value);
      event.stopPropagation();
    }
  };

  /**
   * Keeps the draft name synchronized with the controlled text input.
   */
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDraftName(event.currentTarget.value);
  };

  /**
   * Stores the current edit input without keeping null in React ref state.
   */
  const handleInputRef: React.RefCallback<HTMLInputElement> = inputElement => {
    inputRef.current = inputElement ?? undefined;
  };

  if (isEditing) {
    return (
      <input
        ref={handleInputRef}
        className='browser-window-tab-label-input'
        type='text'
        value={draftName}
        onChange={handleInputChange}
        onKeyDown={handleInputKeyDown}
        onPointerDown={handleInputPointerDown}
      />
    );
  }

  return (
    <span
      className='browser-window-tab-label'
      title={node.getName()}
      onDoubleClick={handleDoubleClick}
    >
      {node.getName()}
    </span>
  );
};

const findFirstTabSet = (node: any): any | undefined => {
  if (!node) {
    return undefined;
  }

  if (node.type === 'tabset') {
    return node;
  }

  for (const child of node.children ?? []) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const tabSet = findFirstTabSet(child);
    if (tabSet) {
      return tabSet;
    }
  }

  return undefined;
};

type LayoutTab = {
  id: string;
  name?: string;
  config?: {
    componentId?: string;
    data?: string;
  };
};

type LayoutJsonNode = {
  type?: string;
  id?: string;
  name?: string;
  selected?: number;
  config?: {
    componentId?: string;
    data?: string;
  };
  children?: LayoutJsonNode[];
};

type WindowBrowserClient = {
  browserContextId: string;
};

type RuntimeConfigPayload = {
  apiServer: string;
  appEnv: string;
  fileServer: string;
  previewMode: string;
  webSocketServer: string;
  mdsWsServer: string;
  oasWsServer: string;
};

/**
 * Captures the browser shell runtime config so cross-origin component iframes can reuse the same application service.
 * Standalone component hosts do not always have the rc/dev runtime values available at their own origin.
 */
const getRuntimeConfigPayload = (): RuntimeConfigPayload => ({
  apiServer: getApiServer(),
  appEnv: getAppEnv(),
  fileServer: getFileServer(),
  previewMode: String(isPreviewMode()),
  webSocketServer: getWebSocketServer(),
  mdsWsServer: getMdsWsServer(),
  oasWsServer: getOasWsServer(),
});

const getTabsFromLayout = (node: LayoutJsonNode | undefined): LayoutTab[] => {
  if (!node) {
    return [];
  }

  if (node.type === 'tab' && typeof node.id === 'string') {
    return [{
      id: node.id,
      name: node.name,
      config: node.config,
    }];
  }

  return (node.children ?? []).flatMap(child => getTabsFromLayout(child));
};

const getAllLayoutTabs = (json: IJsonModel): LayoutTab[] => {
  const layoutTabs = getTabsFromLayout(json.layout as LayoutJsonNode | undefined);
  const borderTabs = (json.borders as LayoutJsonNode[] | undefined ?? []).flatMap(border => getTabsFromLayout(border));
  return [...layoutTabs, ...borderTabs];
};

export const findFirstTabIdByComponentId = (json: IJsonModel, componentId: string): string | undefined =>
  getAllLayoutTabs(json).find(tab => tab.config?.componentId === componentId)?.id;

const isOrderBlotterTab = (tab: LayoutTab): boolean => {
  if (tab.config?.componentId === 'orderBlotter') {
    return true;
  }

  if (tab.name === 'Order Blotter') {
    return true;
  }

  return typeof tab.config?.data === 'string' && tab.config.data.includes('/components/blotter/index.html');
};

export const findFirstOrderBlotterTabId = (json: IJsonModel): string | undefined =>
  getAllLayoutTabs(json).find(tab => isOrderBlotterTab(tab))?.id;

export const focusExistingOrderBlotterTab = (model: Model): boolean => {
  const existingOrderBlotterId = findFirstOrderBlotterTabId(model.toJson());
  if (!existingOrderBlotterId) {
    return false;
  }

  model.doAction(Actions.selectTab(existingOrderBlotterId));
  return dispatchOrderBlotterAddTabEvent(existingOrderBlotterId);
};

export const dispatchOrderBlotterAddTabEvent = (tabId: string): boolean => {
  const iframes = document.querySelectorAll('iframe');
  for (const iframe of iframes) {
    if (iframe.getAttribute('title') !== tabId) {
      continue;
    }

    const iframeOrigin = resolveHttpOrigin(iframe.src, document.baseURI);
    const messageResult = safeParseSchema(orderBlotterAddTabMessageSchema, {
      type: 'order_blotter_add_internal_tab',
    });
    if (!iframe.contentWindow || !iframeOrigin || !messageResult.success) {
      return false;
    }

    iframe.contentWindow.postMessage(messageResult.output, iframeOrigin);
    return true;
  }

  return false;
};

const ensureTopTabSet = (json: IJsonModel): any => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const topTabSet = findFirstTabSet(json.layout);
  if (topTabSet) {
    return topTabSet;
  }

  const createdTabSet = {
    type: 'tabset',
    selected: 0,
    children: [],
  };

  if (!json.layout || typeof json.layout !== 'object') {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    json.layout = {
      type: 'row',
      children: [createdTabSet],
    } as any;
    return createdTabSet;
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const layoutNode = json.layout as any;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  layoutNode.children = Array.isArray(layoutNode.children) ? [createdTabSet, ...layoutNode.children] : [createdTabSet];
  return createdTabSet;
};

export const moveBorderTabsToTopTabSet = (json: IJsonModel): IJsonModel => {
  const clone = structuredClone(json);
  const borderTabs = (clone.borders ?? []).flatMap(border => border.children ?? []);

  if (borderTabs.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const topTabSet = ensureTopTabSet(clone);
    // Preserve existing order in the top tabset, then append moved border tabs.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    topTabSet.children = [...(topTabSet.children ?? []), ...borderTabs];
    if (typeof topTabSet.selected !== 'number' || topTabSet.selected < 0) {
      topTabSet.selected = 0;
    }
  }

  // Remove all border bars so side/bottom borders are no longer rendered.
  clone.borders = [];
  return clone;
};

/** Hosts the Browser workspace layout and owns its external window-event subscriptions. */
export const App = () => {
  const dispatch = useDispatch<AppDispatch>(); // Use the AppDispatch type
  const layoutRef = useRef<ILayoutApi | undefined>(undefined);
  const workspaceJson = useSelector(selectWorkspaceChange);
  const [model, setModel] = React.useState<Model | undefined>(undefined);
  const modelRef: React.MutableRefObject<Model | undefined> = useRef<Model | undefined>(undefined);
  const closeListeners = useRef<Record<string, (parameters?: any) => void>>({});

  /** Registers window-open and iframe-initialization handlers and returns their shared cleanup. */
  const listenForWindowOpen = useCallback(() => {
    const container = getContainer();
    /** Opens a requested component only when it belongs to this Browser context. */
    const handleWindowOpen = (message: {message: QepComponent & {browserContextId?: string; requestingWindowId: string}}) => {
      if (!layoutRef.current) {
        return;
      }

      if (message.message.browserContextId !== container.applicationClient.browserContextId) {
        return;
      }

      const node = modelRef.current?.getNodeById(message.message.requestingWindowId);
      const tabNode = toTabNode(message.message);
      if (node?.getParent()) {
        layoutRef.current.addTabToTabSet(node?.getParent()?.getId() ?? '', tabNode);
        return;
      }

      if (message.message.requestingWindowId) {
        console.warn(`[WindowContainer] Unable to find requesting window ${message.message.requestingWindowId} `
          + `in browser context ${container.applicationClient.browserContextId}; `
          + 'opening in active tab set instead.');
      }

      layoutRef.current.addTabToActiveTabSet(tabNode);
    };

    const openWindowTopic = container.applicationClient.APPLICATION_EVENTS.WINDOW.OPEN_WINDOW.topic;
    container.applicationClient.subscribe(
      openWindowTopic,
      handleWindowOpen,
    );
    // Allow for us to pass info to the iframe for startup
    /** Supplies Browser context and runtime configuration to the iframe that requested initialization. */
    const handleWindowInitialize = (event: MessageEvent<unknown>) => {
      const requestResult = safeParseSchema(windowInitializeRequestSchema, event.data);
      if (!requestResult.success) {
        return;
      }

      const browserClient = container.applicationClient as unknown as WindowBrowserClient;
      const browserContextId = String(browserClient.browserContextId);
      const trustedFrontendOrigins = resolveTrustedFrontendOrigins(getFileServer(), globalThis.location.origin);
      const iframes = document.querySelectorAll('iframe');
      for (const iframe of iframes) {
        const iframeOrigin = resolveHttpOrigin(iframe.src, document.baseURI);
        if (
          iframe.contentWindow === event.source
          && iframeOrigin === event.origin
          && trustedFrontendOrigins.has(event.origin)
        ) {
          const responseResult = safeParseSchema(windowInitializeResponseSchema, {
            type: 'window_initialize',
            browserContextId,
            spawnDataId: iframe.getAttribute('spawndataid'),
            // eslint-disable-next-line @typescript-eslint/naming-convention
            windowID: iframe.getAttribute('title'),
            componentId: iframe.getAttribute('componentid'),
            runtimeConfig: getRuntimeConfigPayload(),
          });
          if (responseResult.success) {
            iframe.contentWindow.postMessage(responseResult.output, iframeOrigin);
          }
        }
      }
    };

    window.addEventListener('message', handleWindowInitialize);

    /** Removes the exact callbacks registered by this mount. */
    return () => {
      container.applicationClient.unSubscribe(openWindowTopic, handleWindowOpen);
      window.removeEventListener('message', handleWindowInitialize);
    };
  }, []);

  /** Registers the window-close handler and returns its cleanup. */
  const listenForWindowClose = useCallback(() => {
    const container = getContainer();
    /** Deletes a requested tab only when it belongs to this Browser context. */
    const handleWindowClose = (message: {message: {browserContextId?: string; windowId: string}}) => {
      if (!layoutRef.current) {
        return;
      }

      if (message.message.browserContextId !== container.applicationClient.browserContextId) {
        return;
      }

      const node = modelRef.current?.getNodeById(message.message.windowId);
      if (node) {
        modelRef.current?.doAction(Actions.deleteTab(node.getId()));
        return;
      }

      console.warn(`[WindowContainer] Close requested for unknown window ${message.message.windowId} in browser context ${container.applicationClient.browserContextId}.`);
    };

    const closeWindowTopic = container.applicationClient.APPLICATION_EVENTS.WINDOW.CLOSE_WINDOW.topic;
    container.applicationClient.subscribe(
      closeWindowTopic,
      handleWindowClose,
    );

    /** Removes the exact close callback registered by this mount. */
    return () => {
      container.applicationClient.unSubscribe(closeWindowTopic, handleWindowClose);
    };
  }, []);

  /** Registers the show-window handler and returns its cleanup. */
  const listenForShowWindow = useCallback(() => {
    const container = getContainer();
    /** Selects a hidden requested tab only when it belongs to this Browser context. */
    const handleShowWindow = (message: {message: {browserContextId?: string; windowId: string}}) => {
      if (message.message.browserContextId !== container.applicationClient.browserContextId) {
        return;
      }

      const node = modelRef.current?.getNodeById(message.message.windowId);
      if (node) {
        if ((node as any).visible === false) {
          modelRef.current?.doAction(Actions.selectTab(node.getId()));
        }

        return;
      }

      console.warn(`[WindowContainer] Show requested for unknown window ${message.message.windowId} `
        + `in browser context ${container.applicationClient.browserContextId}.`);
    };

    const showWindowTopic = container.applicationClient.APPLICATION_EVENTS.WINDOW.SHOW_WINDOW.topic;
    container.applicationClient.subscribe(
      showWindowTopic,
      handleShowWindow,
    );

    /** Removes the exact show callback registered by this mount. */
    return () => {
      container.applicationClient.unSubscribe(showWindowTopic, handleShowWindow);
    };
  }, []);

  /** Registers both scrim handlers and returns their combined cleanup. */
  const listenForScrimChanges = useCallback(() => {
    const container = getContainer();
    /** Shows the loading scrim for a requested tab in this Browser context. */
    const handleShowScrim = (message: {message: {browserContextId?: string; windowId: string}}) => {
      if (message.message.browserContextId !== container.applicationClient.browserContextId) {
        return;
      }

      const node: Node | undefined = modelRef.current?.getNodeById(message.message.windowId);
      if (node) {
        // eslint-disable-next-line unicorn/prefer-query-selector
        document.getElementById(node.getId())?.parentElement?.getElementsByClassName('loadingScrim')[0]?.classList.add('show');
        return;
      }

      console.warn(`[WindowContainer] Scrim show requested for unknown window ${message.message.windowId} `
        + `in browser context ${container.applicationClient.browserContextId}.`);
    };

    const showScrimTopic = container.applicationClient.APPLICATION_EVENTS.WINDOW.SHOW_SCRIM.topic;
    container.applicationClient.subscribe(
      showScrimTopic,
      handleShowScrim,
    );

    /** Hides the loading scrim for a requested tab in this Browser context. */
    const handleHideScrim = (message: {message: {browserContextId?: string; windowId: string}}) => {
      if (message.message.browserContextId !== container.applicationClient.browserContextId) {
        return;
      }

      const node: Node | undefined = modelRef.current?.getNodeById(message.message.windowId);
      if (node) {
        // eslint-disable-next-line unicorn/prefer-query-selector
        document.getElementById(node.getId())?.parentElement?.getElementsByClassName('loadingScrim')[0]?.classList.remove('show');
        return;
      }

      console.warn(`[WindowContainer] Scrim hide requested for unknown window ${message.message.windowId} `
        + `in browser context ${container.applicationClient.browserContextId}.`);
    };

    const hideScrimTopic = container.applicationClient.APPLICATION_EVENTS.WINDOW.HIDE_SCRIM.topic;
    container.applicationClient.subscribe(
      hideScrimTopic,
      handleHideScrim,
    );

    /** Removes the exact scrim callbacks registered by this mount. */
    return () => {
      container.applicationClient.unSubscribe(showScrimTopic, handleShowScrim);
      container.applicationClient.unSubscribe(hideScrimTopic, handleHideScrim);
    };
  }, []);

  // Owns every external listener registered for this App mount.
  useEffect(() => {
    const cleanupWindowOpen = listenForWindowOpen();
    const cleanupWindowClose = listenForWindowClose();
    const cleanupShowWindow = listenForShowWindow();
    const cleanupScrimChanges = listenForScrimChanges();

    // Preload algo definitions
    void dispatch(loadAlgoDefinitions(''));
    /** Releases all application-client and global listeners owned by this mount. */
    return () => {
      cleanupWindowOpen();
      cleanupWindowClose();
      cleanupShowWindow();
      cleanupScrimChanges();
    };
  }, []);

  useEffect(() => {
    let looping = true;
    let raf: number;
    let activeTab: Element | undefined;

    function updateActiveTab(element: Element | undefined) {
      const parentTab = element?.closest('.flexlayout__tab');
      if (parentTab === activeTab) {
        return;
      }

      activeTab?.classList.remove('browser-window-active');
      parentTab?.classList.add('browser-window-active');
      activeTab = parentTab ?? undefined;
    }

    function loop() {
      if (document.activeElement?.tagName === 'IFRAME') {
        updateActiveTab(document.activeElement);
      }

      if (looping) {
        raf = requestAnimationFrame(loop);
      }
    }

    raf = requestAnimationFrame(loop);

    function handleClickOrFocus(event: MouseEvent | FocusEvent) {
      if (event.target instanceof Element) {
        updateActiveTab(event.target);
      } else {
        updateActiveTab(undefined);
      }
    }

    function unselectAll() {
      globalThis.getSelection()?.removeAllRanges();
    }

    document.addEventListener('blur', unselectAll, true);
    document.addEventListener('click', handleClickOrFocus, true);
    document.addEventListener('focus', handleClickOrFocus, true);

    return () => {
      looping = false;
      cancelAnimationFrame(raf);
      document.removeEventListener('blur', unselectAll, true);
      document.removeEventListener('click', handleClickOrFocus, true);
      document.removeEventListener('focus', handleClickOrFocus, true);
      updateActiveTab(undefined);
    };
  }, []);

  /** Applies the deployment's tab-mount policy while materializing the saved workspace model. */
  useEffect(() => {
    if (!workspaceJson) {
      return;
    }

    if (Object.keys(workspaceJson).length === 0) {
      return;
    }

    const workspaceJsonClone = moveBorderTabsToTopTabSet(structuredClone(workspaceJson) as IJsonModel);
    workspaceJsonClone.global ??= {};
    // Overrides for the layout
    workspaceJsonClone.global.tabEnablePopout = false;
    workspaceJsonClone.global.splitterSize = 4;
    workspaceJsonClone.global.tabSetEnableActiveIcon = false;
    // Preserve eager real-time initialization by default, while allowing deployments to opt into lazy mounting.
    workspaceJsonClone.global.tabEnableRenderOnDemand = isWorkspaceTabRenderOnDemand();
    workspaceJsonClone.global.splitterEnableHandle = true;
    workspaceJsonClone.global.tabEnableRename = false;

    workspaceJsonClone.global.tabSetClassNameTabStrip = 'QEP_Tab_Strip';
    const newModel = Model.fromJson(workspaceJsonClone);
    if (workspaceJsonClone?.layout) {// If this is from the default we don't have any ids in the db saved yet
      if (!workspaceJsonClone.layout.id && !workspaceJsonClone.global.qepFallbackWorkspace) {
        void dispatch(saveWorkspaceState(newModel.toJson()));
      }
    } else {
      return;
    }

    setModel(newModel);
    modelRef.current = newModel;
  }, [workspaceJson]);

  // /https://github.com/caplin/FlexLayout/blob/master/examples/demo/App.tsx#L11
  const factory = useCallback((node: TabNode) => {
    // Log lifecycle events
    const component = node.getComponent();
    if (!closeListeners.current[node.getId()]) {
      closeListeners.current[node.getId()] = async () => {
        const linkerGroups = Object.values(DefaultLinkerGroupKeys);
        const windows = await getContainer().linker.getLinkedWindows(linkerGroups);
        if (windows.length > 0) {
          for (const w of windows) {
            for (const g of linkerGroups) {
              void getContainer().linker.deRegisterLink(g, node.getId());
            }
          }
        }

        node.removeEventListener('close');
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete closeListeners.current[node.getId()];
      };

      node.setEventListener('close', closeListeners.current[node.getId()]);
    }

    switch (component) {
      case 'json': {
      // Return (<pre style={{ tabSize: "20px" }} dangerouslySetInnerHTML={{ __html: this.state.json! }} />);

        break;
      }

      case 'selector': {
        return <ComponentSelector layout={layoutRef} model={modelRef} tabId={node.getId()} />;
      }

      case 'grid': {
        if (node.getExtraData().data === null || node.getExtraData().data === undefined) {
          // Create data in node extra data first time accessed
          node.getExtraData().data = makeFakeData();
        }

        return <SimpleTable fields={fields} onClick={() => {/* no-data */}} data={node.getExtraData().data as unknown} />;
      }

      case 'text': {
        try {
          return <div dangerouslySetInnerHTML={{__html: sanitizeWorkspaceHtml(node.getConfig().text as unknown as string)}} />;
        } catch (error) {
          console.error(error);
        }

        break;
      }

      case 'multitype': {
        try {
          const config = node.getConfig() as {type: 'url' | 'html' | 'text'; data: string; spawnDataId?: string; componentId?: string};
          if (config.type === 'url') {
            const nodeContent = (
              <iframe
                id={(node as unknown as any)?.attributes?.id as undefined | string}
                spawndataid={config?.spawnDataId}
                componentid={config?.componentId}
                title={node.getId()}
                src={config.data}
                className='browser-window-iframe'
                width='100%'
                height='100%'
              />
            );
            return (
              <div className='nodeWithScrim'>
                <div className='loadingScrim'>
                  <LoadingIndicator />
                </div>
                {nodeContent}
              </div>
            );
          }

          if (config.type === 'html') {
            return (<div dangerouslySetInnerHTML={{__html: sanitizeWorkspaceHtml(config.data)}} />);
          }

          if (config.type === 'text') {
            return (
              <textarea style={{
                position: 'absolute', width: '100%', height: '100%', resize: 'none', boxSizing: 'border-box', border: 'none',
              }}
              defaultValue={config.data}
              />);
          }
        } catch (error) {
          return (<div>{String(error)}</div>);
        }

        break;
      }

      case undefined: {
        break;
      }
    // No default
    }
    // Else if (component === "tabstorage") {
    //    return <TabStorage tab={node} layout={this.layoutRef!.current!} />
    // }

    return null;
  }, []);

  const onAddFromTabSetButton = (node: TabSetNode | BorderNode) => {
    const addedTab = (layoutRef.current!).addTabToTabSet(node.getId(), {
      component: 'selector',
      name: 'Open component', // Unique?
    });

    requestAnimationFrame(() => {
      if (node.getSelectedNode() === undefined) {
        model?.doAction(Actions.selectTab(addedTab?.getId() ?? ''));
      }
    });
  };

  const onRenderTabSet = (node: (TabSetNode | BorderNode), renderValues: ITabSetRenderValues) => {
    renderValues.stickyButtons.push(<AddSVG alt='Add'
      key='Add button'
      title='Add Tab (using onRenderTabSet callback, see Demo)'
      style={{width: '1.1rem', height: '1.1rem'}}
      // className={styles.flexlayout__tab_toolbar_button}
      onMouseOver={() => {/* no-op */}}
      onClick={() => {
        onAddFromTabSetButton(node);
      }}
    />);
  };

  const onRenderTab = (node: TabNode, renderValues: ITabRenderValues) => {
    renderValues.leading = <Linker node={node} />;
    renderValues.content = <EditableTabLabel node={node} />;
  };

  const onAction = (action: Action): Action | undefined => {
    switch (action.type) {
      case Actions.SELECT_TAB: {
        void onWindowSelected(action.data.tabNode as string);
        break;
      }

      case Actions.DELETE_TAB: {
        void onWindowClosed(action.data.node as string);
        break;
      }

      default: {
        break;
      }
    }

    return action;
  };

  if (!workspaceJson?.global || !model) {
    return <div>Loading...</div>;
  }

  const saveLayout = () => {
    if (workspaceJson.global.qepFallbackWorkspace) {
      return;
    }

    void dispatch(saveWorkspaceState(model?.toJson()));
  };

  const debounceSave = debounce(saveLayout, 1000);
  const shouldRenderChat = getContainer().canUseFeature('chatbox');

  return (
    <QepQueryClientProvider>
      <div className='browser-window-layout'>
        <Toolbar layout={layoutRef} />

        <div className='browser-window-content'>
          <Layout
            ref={layoutRef}
            realtimeResize={true}
            model={model}
            onModelChange={model => {
              debounceSave();
            }}
            onAction={onAction}
            onRenderTab={onRenderTab}
            onRenderTabSet={onRenderTabSet}
            factory={factory} />
        </div>
        {shouldRenderChat ? <Chat/> : null}
      </div>
    </QepQueryClientProvider>
  );
};

export const SimpleTable = (props: {fields: string[]; data: Array<Record<string, string>>; onClick: (...args: any[]) => void}) => {
  const headercells = props.fields.map((field: string) => (<th key={field}>{field}</th>));

  const rows = props.data.map((item, idx) => (
    <tr key={idx}>
      {props.fields.map((field: string) => (
        <td key={field}>{item[field]}</td>
      ))}
    </tr>
  ));

  return <table className='simple_table' onClick={props.onClick}>
    <tbody>
      <tr>{headercells}</tr>
      {rows}
    </tbody>
  </table>;
};

export const makeFakeData = (): Record<string, unknown> => {
  const data: any[] = [];
  const r = Math.random() * 50;

  for (let i = 0; i < r; i++) {
    const rec: Record<string, any> = {};
    rec.Name = randomString(5, 'ABCDEFGHIJKLMNOPQRSTUVWXYZ');
    for (let j = 1; j < fields.length; j++) {
      rec[fields[j]] = (1.5 + (Math.random() * 2)).toFixed(2);
    }

    data.push(rec);
  }

  return {data};
};

export const randomString = (length: number, chars: string) => {
  const a: any[] = [];
  for (let i = 0; i < length; i++) {
    a.push(chars[Math.floor(Math.random() * chars.length)]);
  }

  return a.join('');
};
